/**
 * reasons.service.ts — the account's simple reason lists, as the corporate
 * dashboard edits them:
 *   - Q/A reasons (/v5/qa-reasons; deleted = endDate set),
 *   - membership cancellation reasons (/v5/membership-cancel-reasons;
 *     deleted = active 0).
 * Both answer with `active` already worked out, so one shape serves both.
 * Writes need Admin Access. (Not the generic /admin/qa-reasons resource,
 * whose delete removes the row for good.)
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminReason {
  id: number;
  title: string;
  active: boolean;
}

export interface ReasonApi {
  /** Every reason, deleted ones too. */
  list: () => Promise<AdminReason[]>;
  create: (title: string) => Promise<void>;
  update: (id: number, title: string) => Promise<void>;
  remove: (id: number) => Promise<void>;
  restore: (id: number) => Promise<void>;
}

function reasonApi(base: string, idKey: string): ReasonApi {
  return {
    list: () =>
      apiClient
        .get<ApiResponse<Record<string, unknown>[]>>(base, { params: { include_inactive: 1 } })
        .then((r) =>
          (Array.isArray(r.data?.data) ? r.data.data : []).map((row) => ({
            id: Number(row[idKey] ?? row.id ?? 0),
            title: String(row.title ?? ''),
            active: row.active === true || row.active === 1 || row.active === '1',
          })),
        ),
    create: (title) => apiClient.post(base, { title }).then(() => undefined),
    update: (id, title) => apiClient.put(`${base}/${id}`, { title }).then(() => undefined),
    remove: (id) => apiClient.delete(`${base}/${id}`).then(() => undefined),
    restore: (id) => apiClient.post(`${base}/${id}/restore`).then(() => undefined),
  };
}

export const qaReasonsApi = reasonApi('/qa-reasons', 'qaReasonID');
export const membershipCancelReasonsApi = reasonApi('/membership-cancel-reasons', 'membershipReasonID');
